import { IDecisionPreview } from 'ApiPackage/SearchModule/interfaces/IDecisionPreview';
import { formatDate } from 'CorePackage/MainModule/common/formatDate';
import { DATE_FORMAT_FULL, DATE_FORMAT_LONG } from 'CorePackage/MainModule/constants/dateFormat';
import { parseErknmValue } from 'PassportKnmPackage/MainModule/common/parseErknmValue';
import { fetchReasonTitle } from 'CorePackage/MainModule/common/fieldUtils';
import { getFullName } from 'PassportKnmPackage/MainModule/common/getFullName';

import styles from '../styles.module.scss';
import { KnmSubtitle } from '../KnmSubtitle';
import { EpguNotifications } from '../EpguNotifications';

interface IDecisionCommonProps {
  passportData?: IDecisionPreview;
}

export const DecisionCommon = ({ passportData }: IDecisionCommonProps) => {
  return (
    <div className={styles.ColumnContent}>
      <KnmSubtitle title="Общий состав атрибутов паспорта решения">
        {passportData?.number && (
          <div className={styles.Row}>
            <div className={styles.ColText}>Учётный номер решения</div>
            <div className={styles.ColValue}>{passportData?.number}</div>
          </div>
        )}
        {passportData?.status && (
          <div className={styles.Row}>
            <div className={styles.ColText}>Статус решения</div>
            <div className={styles.ColValue}>{passportData?.status.name}</div>
          </div>
        )}
        {passportData?.basisDocument && (
          <div className={styles.Row}>
            <div className={styles.ColText}>Дата документа</div>
            <div className={styles.ColValue}>
              {formatDate({ date: passportData?.basisDocument.dateTime, dateFormat: DATE_FORMAT_FULL })}
            </div>
          </div>
        )}
        {passportData?.basisDocument && (
          <div className={styles.Row}>
            <div className={styles.ColText}>Номер документа</div>
            <div className={styles.ColValue}>{passportData?.basisDocument.number}</div>
          </div>
        )}
        {passportData?.kind && (
          <div className={styles.Row}>
            <div className={styles.ColText}>Вид контрольного (надзорного) мероприятия, специального режима</div>
            <div className={styles.ColValue}>
              {parseErknmValue<{ title?: string }>(passportData?.kind.value)?.title}
            </div>
          </div>
        )}
      </KnmSubtitle>

      <KnmSubtitle title="Перечень основания проведения">
        {passportData?.reasons?.map((item, index) => (
          <div key={index}>
            <span className={styles.SectionTitle}>Основание проведения</span>
            {item.reason && (
              <div className={styles.Row}>
                <div className={styles.ColText}>Полное наименование основания</div>
                <div className={styles.ColValue}>{fetchReasonTitle(item)}</div>
              </div>
            )}
            {item.reason.type.lastConductedKnmDateRequired && item.reason.lastConductedKnmDate && (
              <div className={styles.Row}>
                <div className={styles.ColText}>Дата</div>
                <div className={styles.ColValue}>
                  {formatDate({ date: item.reason.lastConductedKnmDate, dateFormat: DATE_FORMAT_LONG })}
                </div>
              </div>
            )}
            {item.reason.date && (
              <div className={styles.Row}>
                <div className={styles.ColText}>Дата</div>
                <div className={styles.ColValue}>
                  {formatDate({ date: item.reason.date, dateFormat: DATE_FORMAT_LONG })}
                </div>
              </div>
            )}
            {item.reason.prescriptionDate && (
              <div className={styles.Row}>
                <div className={styles.ColText}>Дата документа</div>
                <div className={styles.ColValue}>
                  {formatDate({ date: item.reason.prescriptionDate, dateFormat: DATE_FORMAT_LONG })}
                </div>
              </div>
            )}
            {item.reason.prescriptionNumber && (
              <div className={styles.Row}>
                <div className={styles.ColText}>Номер документа</div>
                <div className={styles.ColValue}>{item.reason.prescriptionNumber}</div>
              </div>
            )}
            {item.reason.programNumber && (
              <div className={styles.Row}>
                <div className={styles.ColText}>Номер программы проверок</div>
                <div className={styles.ColValue}>{item.reason.programNumber}</div>
              </div>
            )}
            {item.reason.text && (
              <div className={styles.Row}>
                <div className={styles.ColText}>Иное основание</div>
                <div className={styles.ColValue}>{item.reason.text}</div>
              </div>
            )}
            {item.reason.requirementDetails && (
              <div className={styles.Row}>
                <div className={styles.ColText}>Реквизиты требования</div>
                <div className={styles.ColValue}>{item.reason.requirementDetails}</div>
              </div>
            )}
            {item.reason.dateRequirement && (
              <div className={styles.Row}>
                <div className={styles.ColText}>Дата требования</div>

                <div className={styles.ColValue}>
                  {formatDate({ date: item.reason.dateRequirement, dateFormat: DATE_FORMAT_LONG })}
                </div>
              </div>
            )}
            {item.order && (
              <>
                {item.order.guarantors.map((guarantor, guarantorIndex) => {
                  return (
                    <div className={styles.Row} key={guarantorIndex}>
                      <div className={styles.ColText}>ФИО лица, издавшего поручение</div>
                      <div className={styles.ColValue}>{getFullName(guarantor)}</div>
                    </div>
                  );
                })}
                <div className={styles.Row}>
                  <div className={styles.ColText}>Номер поручения</div>
                  <div className={styles.ColValue}>{item.order.number}</div>
                </div>
                <div className={styles.Row}>
                  <div className={styles.ColText}>Дата поручения</div>
                  <div className={styles.ColValue}>
                    {formatDate({
                      date: item.order.date,
                      dateFormat: DATE_FORMAT_LONG,
                    })}
                  </div>
                </div>
              </>
            )}
            {item.riskIndicators?.map((indicatorValue, indicatorIndex) => {
              return (
                <>
                  {indicatorValue.riskIndicator && (
                    <div key={indicatorIndex}>
                      <span className={styles.SectionTitle}>Индикатор риска #{indicatorIndex + 1}</span>
                      <div className={styles.Row}>
                        <div className={styles.ColText}>
                          {parseErknmValue<{ title?: string }>(indicatorValue.riskIndicator.value)?.title}
                        </div>
                      </div>
                    </div>
                  )}
                </>
              );
            })}
            {item.reason?.links && item.reason.links.length > 0 && (
              <KnmSubtitle title="Ссылка на другой паспорт">
                {item.reason.links.map((link, linkIndex) => (
                  <div className={styles.SectionBlock} key={linkIndex}>
                    {link.erpId && (
                      <>
                        <div className={styles.SectionTitle}>Ссылка #{linkIndex + 1}</div>
                        <div className={styles.Row}>
                          <div className={styles.ColText}>Номер КНМ или ПМ</div>
                          <div className={styles.ColValue}>{link.erpId}</div>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </KnmSubtitle>
            )}
          </div>
        ))}
      </KnmSubtitle>

      {!!passportData?.massCancel?.cancelled && (
        <KnmSubtitle title="Сведения о нормативно-правовых основаниях, повлекших за собой необходимость в запуске процесса отмены проведения мероприятий">
          <div className={styles.Row}>
            <div className={styles.ColText}>Основание массовой отмены</div>
            <div className={styles.ColValue}>{passportData.massCancel.reason?.name}</div>
          </div>
          {!!passportData.massCancel.reasonFile?.fileName && (
            <div className={styles.Row}>
              <div className={styles.ColText}>Файл основания массовой отмены</div>
              <div className={styles.ColValue}>
                <a
                  href={`/public/api/inspections/decision/${passportData.id}/mass-cancel-reason-file`}
                  download={passportData.massCancel.reasonFile.fileName}
                >
                  {passportData.massCancel.reasonFile.fileName}
                </a>
              </div>
            </div>
          )}
        </KnmSubtitle>
      )}

      <EpguNotifications epguNotifications={passportData?.epguNotifications} />
    </div>
  );
};
